import { getMockDataAdapter } from '@ascend/mocked-shared/mock-data';

function fmtMillions(value: number): string {
  return `$${(value / 1_000_000).toFixed(1)}M`;
}

const adapter = await getMockDataAdapter();

const [facilities, users, accounts, liquidationsQueue, liquidationsHistory, auctions, kycCases, onchainClaimReviewCases, pools] = await Promise.all([
  adapter.facilities.list(),
  adapter.users.list(),
  adapter.accounts.list(),
  adapter.liquidations.queue(),
  adapter.liquidations.history(),
  adapter.auctions.list(),
  adapter.kyc.listCases(),
  adapter.claimReviews.listOnchainCases(),
  adapter.pools.list(),
]);

// Fetch collateral per margin account and lender counts per pool
const [collateralByAccountArr, lenderCountByPoolArr] = await Promise.all([
  Promise.all(accounts.map((a) => adapter.accounts.collateralByAccountId(a.id))),
  Promise.all(pools.map((p) => adapter.lenderPositions.countByPool(p.id))),
]);
const collateralByAccount = new Map(accounts.map((a, i) => [a.id, collateralByAccountArr[i]] as const));
const lenderCountByPool = new Map(pools.map((p, i) => [p.id, lenderCountByPoolArr[i]] as const));

const facilityById = new Map(facilities.map((item) => [item.id, item]));
const userById = new Map(users.map((item) => [item.id, item]));
const accountById = new Map(accounts.map((item) => [item.id, item]));
const onchainIdsByUserRows = await Promise.all(users.map((user) => adapter.compliance.onchainIdsByUser(user.id)));
const onchainIdsByUser = new Map(users.map((user, idx) => [user.id, onchainIdsByUserRows[idx]] as const));
const onchainIdById = new Map(onchainIdsByUserRows.flatMap((rows) => rows.map((row) => [row.id, row] as const)));

export const ADMIN_DASHBOARD_FACILITIES: Array<{
  id: string;
  name: string;
  status: 'active' | 'paused' | 'shutdown';
  tvl: string;
  utilization: string;
  activeAccounts: number;
  interestRate: string;
  warnings: number;
  defaults: number;
}> = facilities.map((facility, idx) => {
  const scopedAccounts = accounts.filter((account) => account.facilityId === facility.id);
  const status = facility.status === 'shutdown' ? 'shutdown' : facility.status === 'paused' ? 'paused' : 'active';
  const warnings = scopedAccounts.filter((account) => account.status === 'warn').length;
  const defaults = scopedAccounts.filter((account) => account.status === 'default').length;

  return {
    id: `cf-${idx + 1}`,
    name: facility.name,
    status,
    tvl: fmtMillions(facility.tvlUsd),
    utilization: `${facility.utilizationPct.toFixed(1)}%`,
    activeAccounts: scopedAccounts.length,
    interestRate: `${facility.interestRatePct.toFixed(1)}%`,
    warnings,
    defaults,
  };
});

export const ADMIN_MARGIN_ACCOUNTS = accounts
  .map((account) => {
    const facility = facilityById.get(account.facilityId);
    const user = users.find((u) => u.wallet === account.wallet);
    const collaterals = collateralByAccount.get(account.id) ?? [];
    const collateralTokens = collaterals
      .map((c) => `${c.amount.toLocaleString()} ${c.tokenSymbol}`)
      .join(', ');
    return {
      id: account.id,
      wallet: account.wallet,
      status: account.status,
      collateralValue: Math.round(account.collateralValueUsd),
      borrowed: Math.round(account.borrowedUsd),
      availableCredit: Math.round(Math.max(0, account.collateralValueUsd * 0.7 - account.borrowedUsd)),
      healthFactor: Number(account.healthFactor.toFixed(2)),
      lastPayment: account.lastPayment,
      nextPayment: account.nextPayment,
      facilityId: account.facilityId,
      facilityName: facility?.name ?? account.facilityId,
      userName: user?.name ?? account.wallet,
      interestAccrued: Math.round(account.interestAccrued),
      settlementToken: account.settlementToken,
      collateralTokens,
    };
  })
  .sort((a, b) => a.healthFactor - b.healthFactor);

export const ADMIN_MARGIN_ACCOUNT_STATS = {
  totalAccounts: ADMIN_MARGIN_ACCOUNTS.length,
  totalCollateral: ADMIN_MARGIN_ACCOUNTS.reduce((s, a) => s + a.collateralValue, 0),
  totalBorrowed: ADMIN_MARGIN_ACCOUNTS.reduce((s, a) => s + a.borrowed, 0),
  avgHealthFactor:
    ADMIN_MARGIN_ACCOUNTS.length > 0
      ? ADMIN_MARGIN_ACCOUNTS.reduce((s, a) => s + a.healthFactor, 0) / ADMIN_MARGIN_ACCOUNTS.length
      : 0,
};

export const ADMIN_AT_RISK_ACCOUNTS = liquidationsQueue
  .slice(0, 10)
  .map((liq, idx) => ({
    accountId: liq.marginAccountId,
    wallet: liq.wallet,
    priority: (idx + 1) * 11,
    collateralAtRisk: liq.collateralAmountUsd,
    outstandingDebt: liq.debtAmountUsd,
    healthFactor: liq.healthFactor,
    timeInDefault: liq.timeInDefault,
    status: liq.workflowStatus === 'in_progress' ? 'In Progress' : 'Not Started',
  }));

export const ADMIN_LIQUIDATION_QUEUE = liquidationsQueue.map((liq) => {
  const facility = facilityById.get(liq.facilityId);
  return {
    id: liq.id,
    facilityName: facility?.name ?? liq.facilityId,
    accountId: liq.marginAccountId,
    wallet: liq.wallet,
    collateralAmount: liq.collateralAmountUsd,
    debtAmount: liq.debtAmountUsd,
    healthFactor: liq.healthFactor,
    timeInDefault: liq.timeInDefault,
    status: liq.workflowStatus,
    hasDdf: facility?.hasDdf ?? false,
  };
});

export const ADMIN_KYC_CASES = kycCases.map((kyc) => {
  const user = userById.get(kyc.userId);
  const primaryOnchainId = onchainIdsByUser.get(kyc.userId)?.[0];
  return {
    id: kyc.id,
    facilityName: facilityById.get(kyc.facilityId)?.name ?? kyc.facilityId,
    name: user?.name ?? kyc.userId,
    walletAddress: user?.wallet ?? '0x0000000000000000000000000000000000000000',
    onchainId: primaryOnchainId?.address ?? '-',
    investorType: user?.investorType ?? 'Individual',
    jurisdiction: user?.jurisdiction ?? 'Unknown',
    email: user?.email ?? 'unknown@ascend.dev',
    provider: kyc.provider,
    submittedDate: kyc.submittedDate,
    riskScore: kyc.riskScore,
    reviewStatus: kyc.reviewStatus,
  };
});

export const ADMIN_ONCHAIN_CLAIM_CASES = onchainClaimReviewCases.map((claimCase) => {
  const user = userById.get(claimCase.userId);
  const onchainIdentity = onchainIdById.get(claimCase.onchainId);
  return {
    id: claimCase.id,
    facilityName: facilityById.get(claimCase.facilityId)?.name ?? claimCase.facilityId,
    name: user?.name ?? claimCase.userId,
    walletAddress: user?.wallet ?? '0x0000000000000000000000000000000000000000',
    onchainId: onchainIdentity?.address ?? claimCase.onchainId,
    investorType: user?.investorType ?? 'Individual',
    jurisdiction: claimCase.jurisdiction || user?.jurisdiction || 'Unknown',
    email: user?.email ?? 'unknown@ascend.dev',
    claimType: claimCase.claimType,
    provider: claimCase.provider,
    issuer: claimCase.issuer,
    submittedDate: claimCase.submittedDate,
    riskScore: claimCase.riskScore,
    reviewStatus: claimCase.reviewStatus,
  };
});

export const ADMIN_AUCTIONS: Array<{
  id: string;
  account: string;
  title: string;
  currentBid: string;
  reservePrice: string;
  bidCount: number;
  timeRemaining: string;
  status: 'active' | 'ending_soon' | 'settled';
  facilityName: string;
  tokenName: string;
  tokenSymbol: string;
  collateralAmount: number;
  collateralValue: number;
  debtAmount: number;
  settlementToken: string;
}> = auctions.map((auction) => ({
  id: auction.id,
  account: auction.marginAccountId,
  title: auction.title,
  currentBid: `$${auction.currentBidUsd.toLocaleString()}`,
  reservePrice: `$${auction.reservePriceUsd.toLocaleString()}`,
  bidCount: auction.bidCount,
  timeRemaining: auction.timeRemaining,
  status: auction.status,
  facilityName: facilityById.get(auction.facilityId)?.name ?? auction.facilityId,
  tokenName: auction.tokenName,
  tokenSymbol: auction.tokenSymbol,
  collateralAmount: auction.collateralAmount,
  collateralValue: auction.collateralValueUsd,
  debtAmount: auction.debtAmountUsd,
  settlementToken: auction.settlementToken,
}));

export async function getAdminAuctionDetail(auctionId: string, fallbackFacilityName: string) {
  const auction = await adapter.auctions.byId(auctionId);
  if (!auction) {
    return null;
  }

  const bids = await adapter.auctions.bidsByAuctionId(auction.id);
  const timeline: Array<{
    description: string;
    timestamp: string;
    bidder?: string;
    amount?: number;
    txHash?: string;
  }> = [
    {
      description: 'Auction created following liquidation trigger',
      timestamp: '22/03/2026, 19:56:05',
      txHash: bids[0]?.txHash,
    },
    ...bids.map((bid, idx) => ({
      description:
        idx === bids.length - 1
          ? `Current leading bid at $${bid.amountUsd.toLocaleString()}`
          : idx === 0
            ? `First bid placed at $${bid.amountUsd.toLocaleString()}`
            : `Bid increased to $${bid.amountUsd.toLocaleString()}`,
      timestamp: bid.timestamp,
      bidder: bid.bidderWallet,
      amount: bid.amountUsd,
      txHash: bid.txHash,
    })),
  ];

  return {
    id: auction.id,
    facilityName: facilityById.get(auction.facilityId)?.name ?? fallbackFacilityName,
    tokenName: auction.tokenName,
    tokenSymbol: auction.tokenSymbol,
    status: auction.status,
    collateralAmount: auction.collateralAmount,
    collateralValue: auction.collateralValueUsd,
    debtAmount: auction.debtAmountUsd,
    reservePrice: auction.reservePriceUsd,
    currentBid: auction.currentBidUsd,
    bidCount: auction.bidCount,
    timeRemaining: auction.timeRemaining,
    settlementToken: auction.settlementToken,
    timeline,
  };
}

export function getAdminMarginAccountDetail(accountId: string) {
  const account = accounts.find((a) => a.id === accountId);
  if (!account) return null;
  const facility = facilityById.get(account.facilityId);
  const user = users.find((u) => u.wallet === account.wallet);
  const collaterals = collateralByAccount.get(account.id) ?? [];
  const totalCollateralTokens = collaterals.reduce((s, c) => s + c.amount, 0);
  const borrowedUsd = Math.round(account.borrowedUsd);
  const collateralValueUsd = Math.round(account.collateralValueUsd);
  const maxLtvPct = facility?.maxLtvPct ?? 67;
  const maxBorrow = collateralValueUsd * (maxLtvPct / 100);
  const ltv = collateralValueUsd > 0 ? (borrowedUsd / collateralValueUsd) * 100 : 0;
  const dailyAccrual = borrowedUsd * (facility?.interestRatePct ?? 0) / 100 / 365;

  // Find related liquidation queue entries
  const relatedLiquidations = liquidationsQueue.filter((liq) => liq.marginAccountId === accountId);

  return {
    id: account.id,
    wallet: account.wallet,
    status: account.status,
    facilityId: account.facilityId,
    facilityName: facility?.name ?? account.facilityId,
    hasDdf: facility?.hasDdf ?? false,
    userName: user?.name ?? account.wallet,
    settlementToken: account.settlementToken,
    interestRatePct: facility?.interestRatePct ?? 0,
    liquidationThresholdPct: facility?.liquidationThresholdPct ?? 150,
    maxLtvPct,
    collateralValueUsd,
    borrowedUsd,
    interestAccrued: Math.round(account.interestAccrued),
    totalOwed: borrowedUsd + Math.round(account.interestAccrued),
    availableCredit: Math.round(Math.max(0, maxBorrow - borrowedUsd)),
    healthFactor: Number(account.healthFactor.toFixed(2)),
    ltv: Number(ltv.toFixed(1)),
    dailyAccrual: Number(dailyAccrual.toFixed(2)),
    lastPayment: account.lastPayment,
    nextPayment: account.nextPayment,
    collateralBreakdown: collaterals.map((c) => ({
      token: c.tokenSymbol,
      amount: c.amount,
      pctOfTotal: totalCollateralTokens > 0 ? Number(((c.amount / totalCollateralTokens) * 100).toFixed(1)) : 0,
    })),
    isAtRisk: account.healthFactor > 0 && account.healthFactor < 1.5,
    isLiquidatable: account.healthFactor > 0 && account.healthFactor < 1.0,
    inLiquidationQueue: relatedLiquidations.length > 0,
    liquidationStatus: relatedLiquidations[0]?.workflowStatus ?? null,
  };
}

export function getFacilityScopedAccounts(facilityName: string) {
  const facility = facilities.find((item) => item.name === facilityName);
  if (!facility) return ADMIN_MARGIN_ACCOUNTS;
  return ADMIN_MARGIN_ACCOUNTS.filter((account) => account.facilityId === facility.id);
}

export function getFacilityScopedAtRisk(facilityName: string) {
  const facility = facilities.find((item) => item.name === facilityName);
  if (!facility) return ADMIN_AT_RISK_ACCOUNTS;
  return ADMIN_AT_RISK_ACCOUNTS.filter((account) => {
    const src = liquidationsQueue.find((liq) => liq.wallet === account.wallet);
    return src?.facilityId === facility.id;
  });
}

export const ADMIN_LIQUIDATION_HISTORY: Array<{
  account: string;
  type: 'Auction';
  auctionStatus: 'settled' | 'in-progress';
  reserveAdvance: string;
  proceeds: string;
  variance: string;
  finalStatus: 'Settled' | 'Pending';
}> = [
  ...liquidationsHistory.map((liq) => {
    const reserveAdvance = Math.round(liq.debtAmountUsd * 0.85);
    const proceeds = Math.round(liq.collateralAmountUsd * 0.8);
    return {
      account: liq.marginAccountId,
      type: 'Auction' as const,
      auctionStatus: 'settled' as const,
      reserveAdvance: `$${reserveAdvance.toLocaleString()}`,
      proceeds: `$${proceeds.toLocaleString()}`,
      variance: `$${(proceeds - reserveAdvance).toLocaleString()}`,
      finalStatus: 'Settled' as const,
    };
  }),
  ...liquidationsQueue
    .filter((liq) => liq.workflowStatus === 'in_progress')
    .map((liq) => {
      const reserveAdvance = Math.round(liq.debtAmountUsd * 0.85);
      return {
        account: liq.marginAccountId,
        type: 'Auction' as const,
        auctionStatus: 'in-progress' as const,
        reserveAdvance: `$${reserveAdvance.toLocaleString()}`,
        proceeds: '$0',
        variance: '$0',
        finalStatus: 'Pending' as const,
      };
    }),
];

export function getFacilityNameByAccountId(accountId: string): string {
  const account = accountById.get(accountId);
  if (!account) return 'Unknown Facility';
  return facilityById.get(account.facilityId)?.name ?? account.facilityId;
}

export function getFacilityByName(name: string) {
  return facilities.find((f) => f.name === name) ?? null;
}

export function getAllFacilities() {
  return facilities;
}

export async function getCollateralBreakdownByFacility(facilityName: string): Promise<Array<{ token: string; valueUsd: number }>> {
  const facility = facilities.find((f) => f.name === facilityName);
  if (!facility) return [];
  const facilityAccounts = accounts.filter((a) => a.facilityId === facility.id);
  const tokenTotals = new Map<string, number>();
  for (const account of facilityAccounts) {
    const collaterals = await adapter.accounts.collateralByAccountId(account.id);
    if (collaterals.length > 0) {
      // Distribute account's USD value proportionally across its tokens
      const totalTokens = collaterals.reduce((s, c) => s + c.amount, 0);
      for (const c of collaterals) {
        const share = totalTokens > 0 ? (c.amount / totalTokens) * account.collateralValueUsd : 0;
        tokenTotals.set(c.tokenSymbol, (tokenTotals.get(c.tokenSymbol) ?? 0) + share);
      }
    } else {
      // No granular data — attribute evenly across facility's declared tokens
      const tokens = facility.collateralTokens;
      const perToken = account.collateralValueUsd / (tokens.length || 1);
      for (const t of tokens) {
        tokenTotals.set(t, (tokenTotals.get(t) ?? 0) + perToken);
      }
    }
  }
  return Array.from(tokenTotals.entries())
    .map(([token, valueUsd]) => ({ token, valueUsd: Math.round(valueUsd) }))
    .sort((a, b) => b.valueUsd - a.valueUsd);
}

// ─── Lending Pools ───────────────────────────────────────────────────────────

export const ADMIN_LENDING_POOLS = pools.map((pool) => {
  const facility = facilityById.get(pool.facilityId);
  return {
    id: pool.id,
    name: pool.name,
    facilityId: pool.facilityId,
    facilityName: facility?.name ?? pool.facilityId,
    tvlUsd: pool.tvlUsd,
    utilizationPct: pool.utilizationPct,
    apyPct: pool.apyPct,
    lenderCount: lenderCountByPool.get(pool.id) ?? 0,
    status: pool.utilizationPct > 90 ? 'critical' as const : pool.utilizationPct > 80 ? 'high' as const : 'normal' as const,
  };
});

export const ADMIN_POOL_STATS = {
  totalTvl: ADMIN_LENDING_POOLS.reduce((s, p) => s + p.tvlUsd, 0),
  avgApy: ADMIN_LENDING_POOLS.length > 0
    ? ADMIN_LENDING_POOLS.reduce((s, p) => s + p.apyPct, 0) / ADMIN_LENDING_POOLS.length
    : 0,
  avgUtilization: ADMIN_LENDING_POOLS.length > 0
    ? ADMIN_LENDING_POOLS.reduce((s, p) => s + p.utilizationPct, 0) / ADMIN_LENDING_POOLS.length
    : 0,
  activePoolCount: ADMIN_LENDING_POOLS.length,
};

export async function getPoolDetail(poolId: string) {
  const pool = await adapter.pools.byId(poolId);
  if (!pool) return null;

  const facility = facilityById.get(pool.facilityId);
  const [positions, ious, iouEvents] = await Promise.all([
    adapter.lenderPositions.listByPool(poolId),
    adapter.ious.listByPool(poolId),
    adapter.iouEvents.listByPool(poolId),
  ]);

  const totalDeposited = positions.reduce((s, p) => s + p.depositedUsd, 0);
  const totalEarnedYield = positions.reduce((s, p) => s + p.earnedYieldUsd, 0);
  const totalLpTokens = positions.reduce((s, p) => s + p.lpTokens, 0);

  // Build per-lender breakdown with wallet addresses
  const lenders = await Promise.all(
    positions.map(async (pos) => {
      const user = users.find((u) => u.id === pos.userId);
      // Calculate IOU share for this lender (proportional to deposit)
      const iouTotal = ious.reduce((s, iou) => s + iou.remainingAmountUsd, 0);
      const depositShare = totalDeposited > 0 ? pos.depositedUsd / totalDeposited : 0;
      const iouShare = Math.round(iouTotal * depositShare);

      return {
        userId: pos.userId,
        wallet: user?.wallet ?? '0x0000000000000000000000000000000000000000',
        name: user?.name ?? pos.userId,
        depositedUsd: pos.depositedUsd,
        lpTokens: pos.lpTokens,
        earnedYieldUsd: pos.earnedYieldUsd,
        withdrawablePct: pos.withdrawablePct,
        sharePct: totalDeposited > 0 ? (pos.depositedUsd / totalDeposited) * 100 : 0,
        iouShareUsd: iouShare,
      };
    }),
  );

  return {
    id: pool.id,
    name: pool.name,
    facilityId: pool.facilityId,
    facilityName: facility?.name ?? pool.facilityId,
    tvlUsd: pool.tvlUsd,
    utilizationPct: pool.utilizationPct,
    apyPct: pool.apyPct,
    lenderCount: positions.length,
    totalDeposited,
    totalEarnedYield,
    totalLpTokens,
    lenders: lenders.sort((a, b) => b.depositedUsd - a.depositedUsd),
    ious: ious.map((iou) => ({
      ...iou,
      events: iouEvents.filter((e) => e.iouId === iou.id),
    })),
  };
}

export { adapter };
